import { FormControl, Stack, InputLabel, Box, Select, MenuItem, Typography, Dialog, DialogTitle, List, ListItem, ListItemText, DialogContent, FormControlLabel, Switch, DialogActions, Button } from "@mui/material";
import useDealBuilderInputsStore from "../stores/deal-builder-inputs.store.ts";
import InfoOutlineIcon from '@mui/icons-material/Info';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ExpandedRatingOptions, RatingAssumption, SaleMethod, SecurityType, TaxDesignation, USState } from "./services/utils/primary-constants.ts";
import { DatePicker } from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import { useState } from "react";
import useDealBuilderResultStore from "../stores/deal-builder-result.store.ts";
import BlurChangeInput from "./pages/bonds-page/BlurChangeInput.tsx";
import { DealBuilder } from "./services/deal-builder.ts";
import BlurDatePicker from "./pages/bonds-page/blur-date-picker.tsx";
import CurrencyInput from "./pages/bonds-page/currency-input.tsx";
import useSimilarDealsAPI from "./hooks/useSimilarDealsAPI.ts";

const INFORMATION_DIALOGS: any = {
  "report_titles": {
    "title": "Report Titles",
    "settings": {
      "Borrower": "Name of the borrower; the party borrowing bond proceeds from the issuer.",
      "Issuance Title": "Name of the bond issuance title.",
    }
  },
  "pricing_assumptions": {
    "title": "Pricing Assumptions Information",
    "settings": {
      "Market Conditions Date": "The present date that will be assumed when calculating the yield of the different bonds of the series.",
      "State of Issuance": "State in which the bond will be issued.",
      "Rating Assumption": "Rating of the bond.",
      "Tax Designation": "Tax designation of the bond.",
      "Sale Method": "The method that will be used to sell the bonds.",
      "Use Call Date": "Flag indicating if the bond issuer has the right to call the bonds at a specific date.",
      "Call Date": "Date in which the issuer can call the bonds before maturity.",
      "State Enhancement": "Flag indicating if the state issuing the bond is pleadging to take certain actions, such as intercepting state aid or temporarily using state funds to pay the interest or principal if the bond issuer is unable to do so.",
      "Bond Insurance": "Flag indicating if an insurance company has been contracted to pay the interest or principal payments if the bond issuer is unable to do so.",
    }
  },
  "amortization_assumptions": {
    "title": "Pricing Assumptions Information",
    "settings": {
      "Par Amount": "Total amount of money borrowed by the issuer. It's alculated by adding the principals of all the bonds in the series.",
      "Denomination": "Dollar amount on the front of the bonds. Also known as bond resolution.",
      "Delivery Date": "Date in which the bond starts accruing interest.",
      "First Coupon Date": "The date in which the first principal payment to the bondholders is due.",
      "First Principal Date": "The date in which the first principal payment to the bondholders is due.",
      "Final Principal Date": "The date in which the final principal payment to the bondholders is due.",
      "Principal Frequency": "The frequency in which bonds will be issued.",
    }
  },
  "term_bonds": {
    "title": "Group bond principals into a single maturity data.",
    "settings": {
    }
  }
};

export default function DealBuilderInputPanel() {
  const { 
    toggledSections, setToggledSections,
    borrower, setBorrower,
    issuanceTitle, setIssuanceTitle,
    parAmount, setParAmount,
    denomination, setDenomination,
    stateOfIssuance, setStateOfIssuance,
    securityType, setSecurityType,
    ratingAssumption, setRatingAssumption,
    taxDesignation, setTaxDesignation,
    saleMethod, setSaleMethod,
    stateEnhancement, setStateEnhancement,
    bondInsurance, setBondInsurance,
    marketConditionsDate, setMarketConditionsDate,
    deliveryDate, setDeliveryDate,
    firstCouponDate, setFirstCouponDate,
    firstPrincipalDate, setFirstPrincipalDate,
    finalPrincipalDate, setFinalPrincipalDate,
    callDate, setCallDate,
    useCallDate, setUseCallDate,
    principalRate, setPrincipalRate
  } = useDealBuilderInputsStore();

  const { DealBuilderService: builder, setDealBuilderService: setBuilder } = useDealBuilderResultStore();
  const { updateSimilarDeals } = useSimilarDealsAPI();

  const [visibleInfoMenu, setVisibleInfoMenu] = useState<string>();
  const [deleteOutOfSchedule, setDeleteOutOfSchedule] = useState(true);
  const [addMissingBonds, setAddMissingBonds] = useState(true);
  const [showFrequencyDialog, setShowFrequencyDialog] = useState(0);

  // Toggle the different sections visibilities
  function toggleSection(section: string) {
    if (toggledSections.includes(section)) {
      setToggledSections(toggledSections.filter(x => x !== section));
    } else {
      setToggledSections([ ...toggledSections, section ]);
    }
  }

  function isToggled(section: string) {
    return toggledSections.includes(section);
  }

  function getNextCallDate(callDate: boolean, builder: DealBuilder): Moment | undefined {
    if (!callDate) {
      return undefined;
    } else if (builder.bonds.length === 0) {
      return builder.firstCouponDate.clone().add(7, "years");
    } else {
      return moment(builder.bonds[0].maturity).clone().add(7, "years");
    }
  }

  function isAlignmentValid(date1: Moment, date2: Moment, frequency: number): boolean {
    if (frequency <= 0) return false;
    if (date1.date() !== date2.date()) return false;
    if (frequency === 1) return date1.month() === date2.month() && date1.date() === date2.date();

    const interval = 12 / frequency;
    if (!Number.isInteger(interval)) {
      throw new Error(`Frequency ${frequency} does not evenly divide 12 months.`);
    }

    const diffMonths360 = Math.abs((date2.year() - date1.year()) * 12 + (date2.month() - date1.month()));
    return diffMonths360 % interval === 0;
  }

  function onFrequencyMenuClick(freq: number) {
    if (!builder) {
      setPrincipalRate(freq);
    } else {
      setShowFrequencyDialog(freq);
    }
  }

  async function updateBuilderFrequency(freq: number) {
    if (!builder) return;

    builder.setPrincipalFrequency(freq, deleteOutOfSchedule, addMissingBonds);
    builder.updateDebtService(true);
    setBuilder(builder);
    await builder.updateProduction(builder.bonds);
    setBuilder(builder);
  }
  
  return (
    <Box sx={{ 
      flex: 1,
      background: "white", 
      minWidth: "355px",
      borderRight: "1px solid rgba(0, 0, 0, 0.12)",  
    }}>
      <Stack direction="column">
        <Stack direction="row" alignItems="center" mt={1}>
          <InfoOutlineIcon 
            cursor="pointer" 
            fontSize="small"
            sx={{ ml: 1 }} 
            color="info" 
            onClick={() => setVisibleInfoMenu("report_titles")}
          />

          <Box sx={{ 
            marginLeft: "8px",
            fontFamily: "Open Sans",
            color: "#3a3a3a",
            fontSize: "13px",
            marginY: "8px",
            fontWeight: "400"
            }}
          >REPORT TITLES</Box>

          <Box sx={{ flex: 1 }} />

          <KeyboardArrowDownIcon 
            cursor="pointer" 
            onClick={() => toggleSection("report_titles")}
            sx={{ mr: 0.5, rotate: `${isToggled("report_titles") ? "180" : "0"}deg` }}
          />
        </Stack>

        {!toggledSections.includes("report_titles") && (
          <Stack direction="column" m={1} gap={2}>
            <BlurChangeInput
              variant="outlined"
              label="Borrower"
              value={builder ? builder.borrower : borrower}
              size="small"
              sx={{ width: "100%", m: 0 }}
              onBlurChange={(value) => {
                if (builder) {
                  builder.borrower = value;
                  setBuilder(builder);
                } else {
                  setBorrower(value)
                }
              }}
            />
            <BlurChangeInput
              variant="outlined"
              label="Issuance Title"
              value={builder ? builder.issuanceTitle : issuanceTitle}
              size="small"
              sx={{ width: "100%", m: 0 }}
              onBlurChange={(value: string) => {
                if (builder) {
                  builder.issuanceTitle = value;
                  setBuilder(builder);
                } else {
                  setIssuanceTitle(value)
                }
              }}
            />
          </Stack>
        )}
        
        <Stack direction="row" alignItems="center" mt={1}>
          <InfoOutlineIcon 
            cursor="pointer" 
            fontSize="small"
            sx={{ ml: 1 }} 
            color="info" 
            onClick={() => setVisibleInfoMenu("pricing_assumptions")}
          />

          <Box sx={{ 
            marginLeft: "8px",
            fontFamily: "Open Sans",
            color: "#3a3a3a",
            fontSize: "13px",
            marginY: "8px",
            fontWeight: "400"
            }}
          >PRICING ASSUMPTIONS</Box>

          <Box sx={{ flex: 1 }} />

          <KeyboardArrowDownIcon 
            cursor="pointer" 
            onClick={() => toggleSection("pricing_assumptions")}
            sx={{ mr: 0.5, rotate: `${isToggled("pricing_assumptions") ? "180" : "0"}deg` }}
          />
        </Stack>

        {!toggledSections.includes("pricing_assumptions") && (
          <Stack direction="column" m={1} gap={2}>
              <BlurDatePicker 
                label="Market Conditions Date"
                format="MM/DD/YYYY"
                value={builder ? builder.marketConditionsDate : marketConditionsDate}
                sx={{ flex: 1, width: "100%" }}
                onChange={async (value) => {
                  if (builder) {
                    if (value.isSame(builder.marketConditionsDate, "day")) return;
                    builder.marketConditionsDate = value;
                    setBuilder(builder);
                    updateSimilarDeals(builder);
                    await builder.updateProduction(builder.bonds);
                    setBuilder(builder);
                  } else {
                    setMarketConditionsDate(value);
                  }
                }}
                slotProps={{ textField: { size: 'small', sx: { width: '100%' }} }}
                isDateValid={(date: Moment) => {
                  if (date.isBefore(moment("2025-01-01"), "day")) return { valid: false, error: "The date of market conditions is too old." };
                  if (date.isAfter(moment(), "day")) return { valid: false, error: "The date of market conditions can't be in the future." };
                  const firstCoupon = builder ? builder.firstCouponDate : firstCouponDate;
                  const valid = firstCoupon.isAfter(date);
                  if (valid) return { valid: true };
                  return { valid: false, error: "The date of market conditions must be before the first coupon date" };
                }}
              />

              <FormControl fullWidth>
                <InputLabel>State of Issuance</InputLabel>
                <Select
                  value={builder ? builder.stateOfIssuance : stateOfIssuance}
                  label="State of Issuance"
                  size="small"
                  onChange={async (e) => {
                    if (builder) {
                      builder.stateOfIssuance = e.target.value as USState;
                      setBuilder(builder);
                      updateSimilarDeals(builder);
                      await builder.updateProduction(builder.bonds);
                      setBuilder(builder);
                    } else {
                      setStateOfIssuance(e.target.value as USState)
                    }
                  }}
                >
                  {Object.values(USState).map(val => (
                    <MenuItem key={val} value={val}>{val}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Security Type</InputLabel>
                <Select
                  value={builder ? builder.securityType : securityType}
                  label="Security Type"
                  size="small"
                  onChange={async (e) => {
                    if (builder) {
                      builder.securityType = e.target.value as SecurityType;
                      setBuilder(builder);
                      updateSimilarDeals(builder);
                      await builder.updateProduction(builder.bonds);
                      setBuilder(builder);
                    } else {
                      setSecurityType(e.target.value as SecurityType)
                    }
                  }}
                >
                  {Object.values(SecurityType).map(val => (
                    <MenuItem key={val} value={val}>{val}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth>
                <InputLabel>Rating Assumption</InputLabel>
                <Select
                  value={builder ? builder.ratingAssumption : ratingAssumption}
                  label="Rating Assumption"
                  size="small"
                  onChange={async (e) => {
                    if (builder) {
                      builder.ratingAssumption = e.target.value as RatingAssumption;
                      setBuilder(builder);
                      updateSimilarDeals(builder);
                      await builder.updateProduction(builder.bonds);
                      setBuilder(builder);
                    } else {
                      setRatingAssumption(e.target.value as RatingAssumption)
                    }
                  }}
                >
                  {Object.values(RatingAssumption).map(val => (
                    <MenuItem key={val} value={val}>{val}</MenuItem>
                  ))}
                </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Tax Designation</InputLabel>
              <Select
                value={builder ? builder.taxDesignation : taxDesignation}
                label="Tax Designation"
                size="small"
                onChange={async (e) => {
                  if (builder) {
                    const toTaxable = (
                      builder.taxDesignation !== TaxDesignation.Taxable && 
                      e.target.value === TaxDesignation.Taxable
                    );

                    const fromTaxable = (
                      builder.taxDesignation === TaxDesignation.Taxable && 
                      e.target.value !== TaxDesignation.Taxable
                    );

                    builder.taxDesignation = e.target.value as TaxDesignation;
                    setBuilder(builder);
                    updateSimilarDeals(builder);
                    await builder.updateProduction(builder.bonds);

                    if (fromTaxable || toTaxable) {
                      await builder.updateYieldCurve(fromTaxable ? "eod_aaa" : "ust");
                    }

                    setBuilder(builder);
                  } else {
                    setTaxDesignation(e.target.value as TaxDesignation)
                  }
                }}
              >
                {Object.values(TaxDesignation).map(val => (
                  <MenuItem key={val} value={val}>{val}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Sale Method</InputLabel>
              <Select
                value={builder ? builder.saleMethod : saleMethod}
                label="Sale Method"
                size="small"
                onChange={async (e) => {
                  if (builder) {
                    builder.saleMethod = e.target.value as SaleMethod;
                    setBuilder(builder);
                    await builder.updateProduction(builder.bonds);
                    setBuilder(builder);
                  } else {
                    setSaleMethod(e.target.value as SaleMethod)
                  }
                }}
              >
                {Object.values(SaleMethod).map(val => (
                  <MenuItem key={val} value={val}>{val}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>State Enhancement</InputLabel>
              <Select
                value={builder ? builder.stateEnhancement ?? "none" : stateEnhancement ?? "none"}
                label="State Enhancement"
                size="small"
                onChange={async (e) => {
                  const val = e.target.value === "none" ? "" : e.target.value as ExpandedRatingOptions | "";
                  if (builder) {
                    builder.stateEnhancement = val || undefined;
                    setBuilder(builder);
                    updateSimilarDeals(builder);
                    await builder.updateProduction(builder.bonds);
                    setBuilder(builder);
                  } else {
                    setStateEnhancement(val === "" ? undefined : val);
                  }
                }}
              >
                <MenuItem value="none">None</MenuItem>
                {Object.values(ExpandedRatingOptions).map((val) => (
                  <MenuItem key={val} value={val}>
                    {val}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Bond Insurance</InputLabel>
              <Select
                value={builder ? (builder.bondInsurance ? "Yes" : "No" ) : (bondInsurance ? "Yes" : "No")}
                label="Bond Insurance"
                size="small"
                onChange={async (e) => {
                  const checked = e.target.value === "Yes";
                  if (builder) {
                    builder.bondInsurance = checked;
                    setBuilder(builder);
                    await builder.updateProduction(builder.bonds);
                    setBuilder(builder);
                  } else {
                    setBondInsurance(checked);
                  }
                }}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Call Date</InputLabel>
              <Select
                value={builder ? (builder.callDate ? "Yes" : "No" ) : (useCallDate ? "Yes" : "No")}
                label="Call Date"
                size="small"
                onChange={async (e) => {
                  const checked = e.target.value === "Yes";
                  if (builder) {
                    const newCallDate = getNextCallDate(checked, builder);
                    builder.callDate = newCallDate;
                    setBuilder(builder);
                    await builder.updateProduction(builder.bonds);
                    setBuilder(builder);
                  } else {
                    setUseCallDate(checked);
                  }
                }}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>

            {((builder && builder.callDate) || (!builder && useCallDate)) && (
              <DatePicker
                label="Call Date"
                format="MM/DD/YYYY"
                value={callDate.clone()}
                onChange={(value) => value && value.isValid() && setCallDate(moment(value))}
                slotProps={{ textField: { size: 'small', sx: { width: '100%' }} }}
              />
            )}
          </Stack>
        )}

        <Stack direction="row" alignItems="center" mt={1}>
          <InfoOutlineIcon 
            cursor="pointer" 
            fontSize="small"
            sx={{ ml: 1 }} 
            color="info" 
            onClick={() => setVisibleInfoMenu("amortization_assumptions")}
          />

          <Box sx={{ 
            marginLeft: "8px",
            fontFamily: "Open Sans",
            color: "#3a3a3a",
            fontSize: "13px",
            marginY: "8px",
            fontWeight: "400"
            }}
          >AMORTIZATION ASSUMPTIONS</Box>

          <Box sx={{ flex: 1 }} />

          <KeyboardArrowDownIcon 
            cursor="pointer" 
            onClick={() => toggleSection("amortization_assumptions")}
            sx={{ mr: 0.5, rotate: `${isToggled("amortization_assumptions") ? "180" : "0"}deg` }}
          />
        </Stack>
        
        {!toggledSections.includes("amortization_assumptions") && (
          <Stack direction="column" m={1} gap={2}>
            <FormControl fullWidth>
              <CurrencyInput
                value={builder ? builder.totalTargetParAmount : parAmount}
                startAdornment="$"
                locked={undefined}
                onLockedChange={(val) => {}}
                size="small"
                onValidChange={async (value) => {
                  if (builder) {
                    if (builder.totalTargetParAmount === value) return;
                    builder.totalTargetParAmount = value;
                    builder.updateDebtService(true);
                    builder.updateProduction();
                    setBuilder(builder);
                    updateSimilarDeals(builder);
                    await builder.updateProduction(builder.bonds);
                    setBuilder(builder);
                  } else {
                    setParAmount(value);
                  }
                }}
                isValueValid={(value) => {
                  const denom = builder ? builder.denomination : denomination;
                  if (value < denom)  return { valid: false, error: "The total principal must be equal or greater than the denomination." };
                  if (value % denom !== 0) return { valid: false, error: "The total principal must be divisible by the denomination." };
                  return { valid: true };
                }}
              />
            </FormControl>

            <FormControl fullWidth>
              <CurrencyInput
                value={builder ? builder.denomination : denomination}
                locked={undefined}
                onLockedChange={() => {}}
                startAdornment="$"
                size="small"
                isValueValid={(value) => {
                  const principal = builder ? builder.totalTargetParAmount : parAmount;
                  if (value > principal)  return { valid: false, error: "The denominator must be equal or smaller than the total principal." };
                  if(principal % value !== 0) return { valid: false, error: "The denominator must be an exact divisor of the par amount." };
                  return { valid: true };
                }}
                onValidChange={async (value) => {
                  if (builder) {
                    if (builder.denomination === value) return;
                    builder.denomination = value;
                    builder.updateDebtService(true);
                    builder.updateProduction();
                    setBuilder(builder);
                    await builder.updateProduction(builder.bonds);
                    setBuilder(builder);
                  } else {
                    setDenomination(value);
                  }
                }}
              />
            </FormControl>

            <BlurDatePicker 
              label="Delivery Date"
              format="MM/DD/YYYY"
              value={builder ? builder.deliveryDate : deliveryDate}
              sx={{ flex: 1, width: "100%" }}
              onChange={async (value) => {
                if (builder) {
                  if (value.isSame(builder.deliveryDate, "day")) return;
                  builder.deliveryDate = value;
                  builder.updateDebtService();
                  setBuilder(builder);
                  await builder.updateProduction(builder.bonds);
                  setBuilder(builder);
                } else {
                  setDeliveryDate(value);
                }
              }}
              slotProps={{ textField: { size: 'small', sx: { width: '100%' }} }}
              isDateValid={(date: Moment) => {
                if (!date) return { valid: false, error: "Invalid date." };
                const couponDate = builder ? builder.firstCouponDate : firstCouponDate;
                if (date.isSameOrAfter(couponDate)) return { valid: false, error: "The Delivery Date must be before the First Coupon Date." };
                return { valid: true };
              }}
            />

            <BlurDatePicker 
              label="First Coupon Date"
              format="MM/DD/YYYY"
              value={builder ? builder.firstCouponDate : firstCouponDate}
              sx={{ flex: 1, width: "100%" }}
              onChange={async (value) => {
                if (builder) {
                  if (value.isSame(builder.firstCouponDate, "day")) return;
                  const offsetMonths = value.diff(builder.firstCouponDate, "months");
                  builder.firstCouponDate = value;
                  builder.updateMaturitiesAlignment(offsetMonths, value.date());
                  builder.updateFirstAndLastPrincipalDate(
                    builder.getFirstPrincipalDate(firstPrincipalDate), 
                    builder.getLastPrincipalDate(firstPrincipalDate),
                    principalRate
                  );

                  builder.updateDebtService();
                  setBuilder(builder);
                  await builder.updateProduction(builder.bonds);
                  setBuilder(builder);
                } else {
                  setFirstCouponDate(value);
                }
              }}
              slotProps={{ textField: { size: 'small', sx: { width: '100%' }} }}
              isDateValid={(date: Moment) => {
                const delivery = builder ? builder.deliveryDate : deliveryDate;
                const marketDate = builder ? builder.marketConditionsDate : marketConditionsDate;
                if (date.isSameOrBefore(delivery)) return { valid: false, error: "The First Coupon Date must be after the Delivery Date." };
                if (date.isSameOrBefore(marketDate)) return { valid: false, error: "The First Coupon Date must be after the Market Conditions Date." };
                return { valid: true };
              }}
            />

            <BlurDatePicker 
              label="First Principal Date"
              format="MM/DD/YYYY"
              value={builder ? builder.getFirstPrincipalDate(firstPrincipalDate) : firstPrincipalDate}
              sx={{ flex: 1, width: "100%" }}
              onChange={async (value) => {
                if (!builder) {
                  setFirstPrincipalDate(value);
                  return;
                }

                const firstCurrent = builder.getFirstPrincipalDate(firstPrincipalDate);
                if (firstCurrent && firstCurrent.isSame(value, "day")) return;
                const lastPrincipal = builder.getLastPrincipalDate(finalPrincipalDate);
                builder.updateFirstAndLastPrincipalDate(value, lastPrincipal, principalRate);
                builder.updateDebtService(true);
                setBuilder(builder);
                await builder.updateProduction(builder.bonds);
                setBuilder(builder);
              }}
              slotProps={{ textField: { size: 'small', sx: { width: '100%' }} }}
              isDateValid={(date: Moment) => {
                const firstCoupon = builder ? builder.firstCouponDate : firstCouponDate;
                const lastPrincipal = builder ? builder.getLastPrincipalDate(finalPrincipalDate) : finalPrincipalDate;
                if (date.isBefore(firstCoupon, "day")) return { valid: false, error: "The First Principal Date must be the same or after the First Coupon Date." };
                if (date.isAfter(lastPrincipal, "day")) return { valid: false, error: "The First Principal Date must be before or same as the Last Principal Date." };
                if (!isAlignmentValid(firstCoupon, date, 2)) return { valid: false, error: "The First Principal Date doesn't match the coupon schedule." };
                return { valid: true };
              }}
            />

            <BlurDatePicker 
              label="Final Principal Date"
              format="MM/DD/YYYY"
              value={builder ? builder.getLastPrincipalDate(finalPrincipalDate) : finalPrincipalDate}
              sx={{ flex: 1, width: "100%" }}
              onChange={async (value) => {
                if (!builder) {
                  setFinalPrincipalDate(value);
                  return;
                }

                const lastCurrent = builder.getLastPrincipalDate(finalPrincipalDate);
                if (lastCurrent && lastCurrent.isSame(value, "day")) return;
                const firstPrincipal = builder.getFirstPrincipalDate(firstPrincipalDate);
                builder.updateFirstAndLastPrincipalDate(firstPrincipal, value, principalRate);
                builder.updateDebtService(true);
                setBuilder(builder);
                await builder.updateProduction(builder.bonds);
                setBuilder(builder);
              }}
              slotProps={{ textField: { size: 'small', sx: { width: '100%' }} }}
              isDateValid={(date: Moment) => {
                const firstPrincipal = builder ? builder.getFirstPrincipalDate(firstPrincipalDate) : firstPrincipalDate;
                if (date.isBefore(firstPrincipal, "day")) return { valid: false, error: "The Final Principal Date must be after or same as the First Principal Date." };
                if (!isAlignmentValid(firstPrincipal, date, principalRate)) return { valid: false, error: "The Final Principal Date doesn't match the coupon schedule." };
                return { valid: true };
              }}
            />
            
            <FormControl fullWidth>
              <InputLabel>Principal Frequency</InputLabel>
              <Select
                value={principalRate}
                label="Principal Frequency"
                size="small"
              >
                <MenuItem onClick={() => onFrequencyMenuClick(1)} value={1}>Annual</MenuItem>
                <MenuItem onClick={() => onFrequencyMenuClick(2)} value={2}>Semi-Annual</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        )}
      </Stack> 

      {Object.keys(INFORMATION_DIALOGS).map(key => (
        <Dialog 
          key={key}
          open={visibleInfoMenu === key} 
          onClose={() => setVisibleInfoMenu(undefined)}
        >
          <DialogTitle>{INFORMATION_DIALOGS[key].title}</DialogTitle>
          <DialogContent>
            <Typography>
              {INFORMATION_DIALOGS[key].description}
            </Typography>
            {Object.keys(INFORMATION_DIALOGS[key].settings).map(setting => (
              <List key={setting} sx={{ pt: 0 }}>
                <ListItem disablePadding>
                  <ListItemText
                    primary={setting}
                    secondary={INFORMATION_DIALOGS[key].settings[setting]}
                  />
                </ListItem>
              </List>
            ))}
          </DialogContent>
        </Dialog>
      ))}

      <Dialog 
        open={showFrequencyDialog !== 0} 
        onClose={() => setShowFrequencyDialog(0)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Are you sure you want to update the frequency of your bonds?
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={deleteOutOfSchedule}
                  onChange={(e) => setDeleteOutOfSchedule(e.target.checked)}
                />
              }
              label={
                <Typography>
                  Delete existing bonds out of schedule
                </Typography>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={addMissingBonds}
                  onChange={(e) => setAddMissingBonds(e.target.checked)}
                />
              }
              label={<Typography>Add missing bonds</Typography>}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setShowFrequencyDialog(0)} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={() => {
              updateBuilderFrequency(showFrequencyDialog);
              setPrincipalRate(showFrequencyDialog);
              setShowFrequencyDialog(0);
            }} 
            variant="contained" 
            color="primary"
            sx={{ color: "white" }}
          >
            Accept
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
